import { useEffect, useState } from "react";
import { ExternalLink, Sparkles, AlertTriangle, Bookmark, BookmarkCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatErr } from "@/lib/api";
import { api } from "@/lib/api";
import { toEs } from "@/lib/i18n";

function SaveBtn({ grant, saved, onSaved, es }) {
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (saved) return;
    setBusy(true);
    try { await api.post("/grants/saved", { grant, industry: grant.industry || "" }); onSaved(grant.source_id); toast.success(es ? "Guardada en tu lista" : "Saved to your grants"); }
    catch (e2) { toast.error(formatErr(e2)); } finally { setBusy(false); }
  };
  return (
    <button onClick={save} disabled={busy} title={saved ? "Saved" : "Save grant"} data-testid={`spotlight-save-${grant.source_id}`}
      className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 transition-colors ${saved ? "bg-emerald-50 text-emerald-700" : "bg-slate-900 text-white hover:bg-slate-700"}`}>
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : saved ? <BookmarkCheck className="h-3 w-3" /> : <Bookmark className="h-3 w-3" />}{saved ? (es ? "Guardada" : "Saved") : (es ? "Guardar" : "Save")}
    </button>
  );
}

export function GrantSpotlight({ lang = "en" }) {
  const [d, setD] = useState(null);
  const [saved, setSaved] = useState(new Set());
  useEffect(() => {
    api.get("/journey/spotlight").then((r) => setD({ ...r.data, local: r.data.local.map((g) => ({ ...g, source_id: `ai-${r.data.state}-${g.title}`.slice(0, 120), deadline: g.deadline || "Rolling / varies" })) })).catch(() => setD({ federal: [], local: [] }));
    api.get("/grants/saved").then((r) => setSaved(new Set(r.data.map((s) => s.grant?.source_id)))).catch(() => {});
  }, []);
  const markSaved = (id) => setSaved((s) => new Set([...s, id]));
  const es = lang === "es";
  if (!d) return <div className="mt-6 h-24 rounded-lg bg-slate-50 animate-pulse" data-testid="spotlight-loading" />;
  if (!d.federal.length && !d.local.length) return null;
  return (
    <div className="mt-6" data-testid="grant-spotlight">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-amber-500" />
        {es ? `Subvenciones abiertas para ${(d.industry && (toEs(d.industry) || d.industry)) || "tu negocio"}` : `Open grants for ${d.industry || "your business"}`}</div>
      <div className="grid md:grid-cols-3 gap-3 mt-3">
        {d.federal.map((g) => (
          <div key={g.source_id} className="border border-slate-200 rounded-lg p-4 hover:border-emerald-500 transition-colors" data-testid={`spotlight-federal-${g.source_id}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">{es ? "Federal · Grants.gov" : "Federal · Grants.gov"}</div>
            <a href={g.url} target="_blank" rel="noreferrer" className="block text-sm font-semibold text-slate-900 mt-1 line-clamp-2 hover:text-emerald-700 hover:underline">{g.title}</a>
            <div className="text-xs text-slate-500 mt-1 truncate">{g.agency}</div>
            <div className="text-xs text-slate-600 mt-2 flex items-center justify-between gap-2"><span className="flex items-center gap-1">{es ? "Cierra" : "Closes"} {g.deadline}<ExternalLink className="h-3 w-3" /></span><SaveBtn grant={{ ...g, industry: d.industry }} saved={saved.has(g.source_id)} onSaved={markSaved} es={es} /></div>
          </div>
        ))}
      </div>
      {d.local.length > 0 && (
        <div className="mt-3 grid md:grid-cols-2 gap-3" data-testid="spotlight-local">
          {d.local.map((g, i) => (
            <div key={i} className="border border-dashed border-amber-300 bg-amber-50/40 rounded-lg p-4 hover:border-amber-500 transition-colors" data-testid={`spotlight-local-${i}`}>
              <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{es ? `${d.state} · verifica en el sitio de la agencia` : `${d.state} · verify on agency site`}</div>
              <a href={g.url} target="_blank" rel="noreferrer" className="block text-sm font-semibold text-slate-900 mt-1 hover:text-amber-800 hover:underline">{g.title}</a>
              <div className="text-xs text-slate-500 mt-1 flex items-center justify-between gap-2"><span>{g.agency}{g.amount ? ` · ${g.amount}` : ""}</span><SaveBtn grant={{ ...g, industry: d.industry }} saved={saved.has(g.source_id)} onSaved={markSaved} es={es} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
